import { Component, Host, h, Prop, JSX } from '@stencil/core'
import { Row, Rows, SectionItem } from '~/components/molecules/footer/types'

/**
 * SectionItem component.
 *
 * @param {SectionItem} props - The properties of the SectionItem component.
 */
const SectionItem = ({ href, title, src, alt, target = '_self' }: SectionItem) => href && (
    <a role="menuitem" aria-label={title} href={href} target={target} title={title ?? alt}>{title}</a>) || src && (
    <a role="menuitem" aria-label={title ?? alt} href={href} target={target} title={title ?? alt}><img
        role="img" loading="lazy" src={src} alt={alt ?? title}/>)</a>)

/**
 * WfFooterSitemap component.
 *
 * @example
 * <wf-footer-sitemap rows={rows} />
 */
@Component({
    tag: 'wf-footer-sitemap',
    styleUrl: 'wf-footer-sitemap.scss',
    shadow: true,
})
// eslint-disable-next-line require-jsdoc
export class WfFooterSitemap {
    /**
     * The rows of the sitemap.
     *
     * @type {Rows}
     */
    @Prop() rows!: Rows

    /**
     * The sections of the sitemap.
     *
     * @type {HTMLDivElement[]}
     */
    private sections: HTMLDivElement[] = []
    private activeSectionElement: HTMLElement

    /**
     * Handles the section click event.
     *
     * @param {UIEvent} ev - The UI event.
     */
    handleSectionClick = (ev: UIEvent): void => {
        this.activeSectionElement = (ev.currentTarget as HTMLDivElement).parentElement

        this.collapseAllItems()

        this.toggleActiveSectionDisplay()
    }

    /**
     * Toggles the display of the active section.
     */
    toggleActiveSectionDisplay = () => (this.activeSectionElement.classList.toggle('collapsed'))

    /**
     * Collapses all items.
     */
    collapseAllItems(): void {
        this.sections
            .filter(section => section !== this.activeSectionElement)
            .forEach(section => section.classList.add('collapsed'))
    }

    /**
     * Groups the sections of a row.
     *
     * @param {Row} row - The row to group the sections of.
     * @returns {JSX.Element[]} The grouped sections.
     */
    groupedSections(row: Row): JSX.Element[] {
        return row.columns
            .map(columns => columns.sections
                .map(section => section.items.map(props =>
                    <SectionItem {...props} />
                )).flat()).flat()
    }

    /**
     * @inheritDoc
     */
    render() {
        if (!Array.isArray(this.rows)) return

        return (
            <Host>
                {this.rows.map(row =>
                    <div role="menu" aria-label={row.columns[0].sections[0].headline}
                        class={{ 'wf-footer-sitemap-row': true, 'group': row.isMobileGrouped }}>
                        {row.isMobileGrouped &&
                            <div class="column grouped">
                                <div class="section collapsed" role="group" ref={el => this.sections.push(el)}>
                                    <button role="menuitem" onClick={this.handleSectionClick} class="title">
                                        <span class={{ 'headline': true }}>{row.columns[0].sections[0].headline}</span>
                                        <vx-icon icon="arrow_down"/>
                                    </button>
                                    <div class="content grouped">
                                        {this.groupedSections(row)}
                                    </div>
                                </div>
                            </div>
                        }
                        {row.columns.map(column =>
                            <div class="column">
                                {column.sections.map(({ headline, isPlaceHolderHeadline, items }) =>
                                    <div class="section collapsed" ref={el => this.sections.push(el)}>
                                        <button onClick={!isPlaceHolderHeadline && this.handleSectionClick}
                                            class="title"
                                            disabled={isPlaceHolderHeadline}
                                            aria-label={headline ?? 'placeholder'}>
                                            <span class={{ 'headline': true, placeholder: isPlaceHolderHeadline }}>
                                                {headline}
                                            </span>
                                            <vx-icon icon="arrow_down"/>
                                        </button>
                                        <div class="content" role="group">
                                            {items.map(props => <SectionItem {...props} />)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Host>
        )
    }
}
